import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    settings: {},                 // full settings object from backend
    globalReturnMultiplier: null,
    timer: 0,
    timerDisplay: "00:00",
    phase: "bidding", // bidding, autosubmit, hold
    roundNumber: 0,
    status: "running",
    bidPredictions: {}, // { "0": 5, "1": 12, ... }
    bidDetails: {}, // { "0": { count: 5, totalAmount: 2500 }, "1": { count: 12, totalAmount: 6000 } }
    totalAmount: 0,
    selectedWinner: null,
    isManualWinnerWindowOpen: false,
    lastTenWinners: [], // NEW: Persistent history
    loading: false,
    error: null,
};

const settingsSlice = createSlice({
    name: "roSettings",
    initialState,
    reducers: {
        fetchSettingsStart(state) {
            state.loading = true;
            state.error = null;
        },
        fetchSettingsSuccess(state, action) {
            state.loading = false;
            state.error = null;
            state.settings = action.payload;
            state.globalReturnMultiplier = action.payload.globalReturnMultiplier;

            // Initialize bid state if present in settings
            if (action.payload.bids) {
                state.bidPredictions = action.payload.bids;
            }
            if (action.payload.totalAmount !== undefined) {
                state.totalAmount = action.payload.totalAmount;
            }
            if (action.payload.numberTotals) {
                Object.entries(action.payload.numberTotals).forEach(([num, amount]) => {
                    const numStr = String(num);
                    state.bidDetails[numStr] = {
                        count: state.bidPredictions[numStr] || 0,
                        totalAmount: amount
                    };
                });
            }

            // Sync history if present in settings response
            if (action.payload.lastTenWinners && Array.isArray(action.payload.lastTenWinners)) {
                state.lastTenWinners = action.payload.lastTenWinners;
            }
        },
        fetchSettingsFailure(state, action) {
            state.loading = false;
            state.error = action.payload;
        },
        updateTimer(state, action) {
            const timeLeft = action.payload;
            state.timer = timeLeft;
            // Format timer as MM:SS
            const minutes = Math.floor(timeLeft / 60);
            const seconds = timeLeft % 60;
            state.timerDisplay = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
        },
        updateGameState(state, action) {
            const { timeLeft, phase, roundNumber, status } = action.payload;

            // Update timer
            if (timeLeft !== undefined && timeLeft !== null) {
                const time = Number(timeLeft);
                state.timer = time;
                const minutes = Math.floor(time / 60);
                const seconds = time % 60;
                state.timerDisplay = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
            }

            // Update phase
            if (phase) {
                state.phase = phase;
            }

            // Update round number
            if (roundNumber !== undefined) {
                state.roundNumber = roundNumber;
            }

            // Update status
            if (status) {
                state.status = status;
            }

            // Manual winner window logic - Controlled by backend phase and socket events
            const phaseVal = state.phase;

            // Open window when phase becomes autosubmit
            if (phaseVal === "autosubmit") {
                state.isManualWinnerWindowOpen = true;
            }
        },
        updateBidPredictions(state, action) {
            const bids = action.payload.bids || {};
            const isIncremental = action.payload.isIncremental;

            if (isIncremental) {
                // Add to existing counts
                Object.entries(bids).forEach(([num, count]) => {
                    const numStr = String(num);
                    state.bidPredictions[numStr] = (state.bidPredictions[numStr] || 0) + count;
                });

                // Also update the global total amount if it's there
                if (action.payload.totalAmount !== undefined) {
                    state.totalAmount = action.payload.totalAmount;
                }
            } else {
                // Replace all counts (full sync)
                state.bidPredictions = bids;
                state.totalAmount = action.payload.totalAmount || 0;
            }

            // Sync bidDetails with updated predictions and totals
            if (action.payload.numberTotals) {
                Object.entries(action.payload.numberTotals).forEach(([num, amount]) => {
                    const numStr = String(num);
                    state.bidDetails[numStr] = {
                        count: isIncremental
                            ? (state.bidDetails[numStr]?.count || 0) + (bids[numStr] || 0)
                            : (bids[numStr] || 0),
                        totalAmount: isIncremental
                            ? (state.bidDetails[numStr]?.totalAmount || 0) + amount
                            : amount
                    };
                });
            } else {
                // If no numberTotals, just update counts in existing bidDetails or create new ones
                Object.entries(state.bidPredictions).forEach(([num, count]) => {
                    const numStr = String(num);
                    if (!state.bidDetails[numStr]) {
                        state.bidDetails[numStr] = { count: 0, totalAmount: 0 };
                    }
                    state.bidDetails[numStr].count = count;
                });
            }
        },

        handlePlaceBid(state, action) {
            const { number, amount } = action.payload;

            if (number === undefined || amount === undefined) {
                return;
            }

            const numStr = String(number);

            if (!state.bidDetails[numStr]) {
                state.bidDetails[numStr] = {
                    count: 0,
                    totalAmount: 0
                };
            }

            state.bidDetails[numStr] = {
                count: (state.bidDetails[numStr].count || 0) + 1,
                totalAmount: (state.bidDetails[numStr].totalAmount || 0) + amount
            };

            state.bidPredictions[numStr] = (state.bidPredictions[numStr] || 0) + 1;
            state.totalAmount = (state.totalAmount || 0) + amount;
        },

        setManualWinnerWindow(state, action) {
            state.isManualWinnerWindowOpen = action.payload;
        },
        setSelectedWinner(state, action) {
            state.selectedWinner = action.payload;
        },
        resetRound(state) {
            state.bidPredictions = {};
            state.bidDetails = {};
            state.totalAmount = 0;
            state.selectedWinner = null;
            state.isManualWinnerWindowOpen = false;
        },
        updateHistory(state, action) {
            state.lastTenWinners = action.payload;
        },
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setError(state, action) {
            state.error = action.payload;
            state.loading = false;
        },
    },
});

export const {
    fetchSettingsStart,
    fetchSettingsSuccess,
    fetchSettingsFailure,
    updateTimer,
    updateGameState,
    updateBidPredictions,
    handlePlaceBid,
    setManualWinnerWindow,
    setSelectedWinner,
    resetRound,
    updateHistory,
    setLoading,
    setError,
} = settingsSlice.actions;

export default settingsSlice.reducer;
